import {css} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {UsePlanStudyDesignStateResult} from 'studies/typed/shared/procedure-runs/design-state/usePlanStudyDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {ProcedureChangeParams} from '../procedure-selection/EditProcedureModal';
import SelectedProcedureEditor from '../procedure-selection/SelectedProcedureEditor';
import {FlowchartProcedureRunDesignOptInBanner} from './FlowchartProcedureRunDesignPreviewBanners';
import {DataEntryProps, ProcedureProps} from './ProcedureRunPlanDesignContentTypes';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';

interface LegacyProcedureRunPlanDesignContentProps {
  dataEntry: DataEntryProps;
  designState: UsePlanStudyDesignStateResult;
  editNumConditionGroupsButtons?: JSX.Element;
  onDismissNewUIOptInBanner: () => void;
  onOptIn: () => Promise<void>;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  procedure: ProcedureProps;
  shouldShowNewUIOptInBanner: boolean;
}

export default function LegacyProcedureRunPlanDesignContent({
  dataEntry,
  designState,
  editNumConditionGroupsButtons,
  onDismissNewUIOptInBanner,
  onOptIn,
  onProcedureChange,
  procedure,
  shouldShowNewUIOptInBanner,
}: LegacyProcedureRunPlanDesignContentProps): JSX.Element {
  const {
    context,
    editState,
    experimentalInputs,
    methodExecutionLimitState,
    selectedDesignTabIndex,
    selectedMethodId,
    setExperimentalInputs,
    setSelectedDesignTabIndex,
    setSharedConditions,
    setSidepanelName,
    setSelectedMethodId,
    sidepanelName,
    templateFlowchart,
    uniformPlanRows,
  } = designState;
  const conditions = uniformPlanRows ?? [];
  const isSharedTableLocked = editState !== StudyDesignTabEditState.EDITING || uniformPlanRows == null;

  // If the flowchart procedure run design is not enabled, we should have a selected method id
  assert(
    selectedMethodId != null,
    'Expected selected method id to be non-null for non-flowchart (legacy) procedure run design'
  );
  return (
    <div
      className={css(studyDesignTabStyles.leftSidepanelContainer)}
      {...getTestProps({component: 'LegacyProcedureRunPlanDesignContent', element: 'root'})}
    >
      <FlowchartSidebar
        icons={[
          {
            name: 'note-table',
            onClick: () => {
              setSidepanelName(PARAMETER_VALUES_TAB_NAME);
            },
          },
          {
            name: 'settings',
            onClick: () => {
              setSidepanelName(SETTINGS_TAB_NAME);
            },
          },
        ]}
        selectedIconIndex={selectedDesignTabIndex}
        setSelectedIconIndex={setSelectedDesignTabIndex}
      />
      <ContextPanel
        sidepanelName={sidepanelName}
        sidepanelHeaderContent={
          selectedDesignTabIndex === 0 &&
          editState === StudyDesignTabEditState.EDITING &&
          editNumConditionGroupsButtons != null
            ? editNumConditionGroupsButtons
            : undefined
        }
      >
        {selectedDesignTabIndex === 0 ? (
          <>
            {shouldShowNewUIOptInBanner && (
              <div className={css(studyDesignTabStyles.previewBannerContainer)}>
                <FlowchartProcedureRunDesignOptInBanner
                  isPlan={true}
                  procedureType={procedure.type}
                  onDismiss={onDismissNewUIOptInBanner}
                  onOptIn={onOptIn}
                />
              </div>
            )}
            <ParameterValuesTabContents
              methods={templateFlowchart.nodes}
              procedureFlowchart={templateFlowchart}
              selectedMethodId={selectedMethodId}
              onChangeSelectedMethod={setSelectedMethodId}
              procedureType={procedure.type}
              context={context}
            />
          </>
        ) : (
          <>
            <SettingsTabContents
              conditions={conditions}
              setConditions={setSharedConditions}
              experimentalInputs={experimentalInputs}
              setExperimentalInputs={setExperimentalInputs}
              locked={isSharedTableLocked}
              procedureType={procedure.type}
              methodExecutionLimitState={methodExecutionLimitState}
              studyType={procedure.studyType}
              dataEntryMode={dataEntry.mode}
              estimateResult={{stale: false, executionPlan: null}}
              setDataEntryMode={dataEntry.setMode}
              isPlan={true}
            />
            <SelectedProcedureEditor
              isEditable={editState === StudyDesignTabEditState.EDITING}
              procedureId={procedure.id}
              flowchartVersionId={procedure.flowchartVersionId}
              procedureType={procedure.type}
              studyType={procedure.studyType}
              onProcedureChange={onProcedureChange}
            />
          </>
        )}
      </ContextPanel>
    </div>
  );
}
